/**
 * The editable form of a TimeRule (fixed time, or zman + offset + rounding),
 * shared by the minyan form and the opening-hours form.
 */
import type { RoundDirection, TimeRule } from '../types';
import { MYZMANIM_FIELDS } from '../config/constants';

export const ROUND_TO_OPTIONS = [5, 10, 15, 30] as const;
export type RoundTo = (typeof ROUND_TO_OPTIONS)[number];
export const ROUND_DIRECTIONS: RoundDirection[] = ['up', 'down', 'nearest'];
export const ZMAN_KEYS = Object.keys(MYZMANIM_FIELDS);

export interface TimeRuleDraft {
  ruleType: 'fixed' | 'zmanOffset';
  fixedTime: string;
  baseZman: string;
  offsetMinutes: number;
  roundingEnabled: boolean;
  roundTo: RoundTo;
  roundDirection: RoundDirection;
}

/** Form values for a rule; `defaultTime` is used when there is no fixed time yet. */
export function timeRuleToDraft(rule: TimeRule | undefined, defaultTime = '08:00'): TimeRuleDraft {
  return {
    ruleType: rule?.type ?? 'fixed',
    fixedTime: rule?.type === 'fixed' ? rule.time : defaultTime,
    baseZman: rule?.type === 'zmanOffset' ? rule.baseZman : ZMAN_KEYS[0],
    offsetMinutes: rule?.type === 'zmanOffset' ? rule.offsetMinutes : 0,
    roundingEnabled: rule?.type === 'zmanOffset' && !!rule.rounding,
    roundTo: rule?.type === 'zmanOffset' && rule.rounding ? rule.rounding.to : 5,
    roundDirection: rule?.type === 'zmanOffset' && rule.rounding ? rule.rounding.direction : 'up',
  };
}

/** The rule to store. Same result the minyan form always produced. */
export function draftToTimeRule(draft: TimeRuleDraft): TimeRule {
  if (draft.ruleType === 'fixed') {
    return { type: 'fixed', time: draft.fixedTime };
  }
  return {
    type: 'zmanOffset',
    baseZman: draft.baseZman,
    offsetMinutes: Number(draft.offsetMinutes) || 0,
    ...(draft.roundingEnabled ? { rounding: { to: draft.roundTo, direction: draft.roundDirection } } : {}),
  };
}
